import React, { useRef, useState } from 'react';
import { Button, Input, Modal } from 'antd';
import type { InputRef } from 'antd';
import type { MapFeature } from '../types';
import { calculateAreaInHectares } from '../mapUtils';

// ─────────────────────────────────────────────────────────────
// PaddockUpdateAreaModal
// Centred modal for manually overriding a paddock's stored area.
//   • Pre-fills the current area value
//   • Validates: numeric, > 0, max 4 decimal places
//   • Phase 1: onSave is synchronous (local state only, no API call).
// ─────────────────────────────────────────────────────────────

interface Props {
  paddock: MapFeature;
  onSave: (area: number) => void;
  onCancel: () => void;
}

const PaddockUpdateAreaModal = ({ paddock, onSave, onCancel }: Props) => {
  const unit = paddock.areaUnit ?? 'ha';
  const [value, setValue] = useState(String(paddock.area));
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<InputRef>(null);

  const geometricArea: number | null = (() => {
    const ring = paddock.geometry?.coordinates?.[0];
    if (!ring || ring.length < 3) return null;
    return calculateAreaInHectares({ type: 'Feature', geometry: paddock.geometry, properties: {} });
  })();

  const handleUseGeometric = () => {
    if (geometricArea === null) return;
    setValue(String(geometricArea));
    if (error) setError(null);
    inputRef.current?.input?.focus();
  };

  const handleAfterOpen = (open: boolean) => {
    if (!open) return;
    const el = inputRef.current?.input;
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (error) setError(null);
  };

  const handleSave = () => {
    const trimmed = value.trim();
    const parsed = Number(trimmed);
    if (!trimmed || isNaN(parsed) || parsed <= 0) {
      setError('Please enter a valid area greater than 0.');
      return;
    }
    // Round to 2 dp to stay consistent with zone.zoneArea storage.
    onSave(Math.round(parsed * 100) / 100);
  };

  return (
    <Modal
      open
      title="Update Area"
      okText="Save"
      cancelText="Cancel"
      onOk={handleSave}
      onCancel={onCancel}
      mask={{ closable: false }}
      destroyOnHidden
      afterOpenChange={handleAfterOpen}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div className="flex items-center justify-between">
          <Button
            disabled={geometricArea === null}
            onClick={handleUseGeometric}
          >
            Use geometric area
          </Button>
          <div className="flex gap-2">
            <CancelBtn />
            <OkBtn />
          </div>
        </div>
      )}
    >
      <div className="mt-2">
        <Input
          ref={inputRef}
          type="number"
          value={value}
          onChange={handleChange}
          onPressEnter={handleSave}
          suffix={unit}
          status={error ? 'error' : undefined}
          placeholder="Area"
        />
        {error && (
          <p className="mt-1.5 text-xs text-red-500">{error}</p>
        )}
      </div>
    </Modal>
  );
};

export default PaddockUpdateAreaModal;
